import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  canonical?: string;
}

const SEO = ({
  title = "Kente Weave Design - Custom African Graduation Stoles & Sashes",
  description = "Design personalized Kente graduation stoles with authentic African patterns, custom text, and cultural symbols. Create beautiful handwoven or printed Kente sashes for your special graduation day. Order now!",
  keywords = "kente, graduation stoles, african patterns, custom sashes, graduation ceremony, ghanaian culture, traditional weaving, personalized stoles, cultural symbols, adinkra symbols",
  image = "https://kente-design.orctatech.com/og-image.png",
  url = "https://kente-design.orctatech.com",
  type = "website",
  noindex = false,
  canonical,
}: SEOProps) => {
  const fullTitle = title.includes("Kente Weave Design")
    ? title
    : `${title} | Kente Weave Design`;
  const canonicalUrl = canonical || url;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Kente Weave Design" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      <meta property="twitter:image:alt" content={fullTitle} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="application-name" content="Kente Weave Design" />
      <meta name="apple-mobile-web-app-title" content="Kente Weave Design" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Preload critical resources */}
      <link
        rel="preload"
        href="/android-chrome-512x512.png"
        as="image"
        type="image/png"
      />
      <link rel="preload" href="/og-image.png" as="image" type="image/png" />

      {/* DNS Prefetch for external resources */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Kente Weave Design",
          description: description,
          url: url,
          applicationCategory: "DesignApplication",
          operatingSystem: "Web Browser",
          offers: {
            "@type": "Offer",
            price: "85",
            priceCurrency: "GHS",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2025-12-31",
          },
          creator: {
            "@type": "Organization",
            name: "Orcta Technologies",
            url: "https://orctatech.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Orcta Technologies",
            url: "https://orctatech.com",
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
